"use client";

/**
 * Left column of the preview Sheet — three-bucket counter strip + the
 * filtered list of recipient cards (spec §6.2).
 *
 * Filter contract:
 *   - URL `?bucket=` drives `activeBucket` (parent-owned).
 *   - Each `EnrollmentPreviewCandidate.bucket` is the source of truth
 *     for which tab a row appears in. The frontend never re-derives the
 *     bucket — that's the backend's job per spec §16.
 *
 * Empty states:
 *   - When the cohort is empty (every candidate removed) the list shows
 *     a small empty card.
 *   - Per-bucket empty states get their own copy ("No issues yet" /
 *     "Nothing to fix") so the surface doesn't feel broken when the
 *     user removes the last fix-issues candidate.
 *
 * Removal:
 *   - The Remove button on each card calls back to the parent's
 *     `onRemoveCandidate(jobCandidateId)`. The parent owns the PATCH
 *     request and confirm-modal flow so the list stays presentational.
 */

import * as React from "react";

import { AnimatePresence, motion } from "framer-motion";

import { SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

import { BUCKET_META } from "../../lib/issue-meta";
import type {
  EnrollmentPreviewResponse,
  PreviewBucket,
} from "../../lib/types";

/** Bright per-bucket accent for the empty-state icon (matches the bucket
 * counter cards). */
const BUCKET_ICON_COLOR: Record<PreviewBucket, string> = {
  no_issue: "#66dc7e",
  warning: "#ffaf53",
  fix: "#ef4444",
};

import { BucketCounters } from "./bucket-counters";
import { RecipientCard } from "./recipient-card";

export interface RecipientListProps {
  preview: EnrollmentPreviewResponse | null;
  isLoading: boolean;
  activeBucket: PreviewBucket;
  onBucketChange: (next: PreviewBucket) => void;
  selectedJobCandidateId: string | null;
  onSelectCandidate: (id: string) => void;
  onRemoveCandidate: (id: string) => void;
  /** Sender timezone, optional — drives the "First sends" badge. */
  senderTz?: string | null;
  /** Footer Enroll CTA wiring (Figma puts the primary action here). */
  totalCount?: number;
  fixCount?: number;
  warningCount?: number;
  onCommit?: () => Promise<unknown> | void;
  isCommitting?: boolean;
  /** Mobile-only (ARBI-677): the step pane — which owns the desktop close
   * X — is hidden on the mobile list step, so when provided the list
   * header renders its own guarded-close control. Desktop never passes
   * this. */
  onClose?: () => void;
}

const EMPTY_BUCKET_COPY: Record<
  PreviewBucket,
  { title: string; subtitle: string }
> = {
  no_issue: {
    title: "No clean rows yet",
    subtitle:
      "Remove a blocked candidate or fix their override and they'll move here.",
  },
  warning: {
    title: "No warnings",
    subtitle:
      "Daily-limit warnings will land here once the schedule is computed.",
  },
  fix: {
    title: "Nothing to fix",
    subtitle:
      "Every candidate in this cohort can enroll without an override.",
  },
};

export function RecipientList({
  preview,
  isLoading,
  activeBucket,
  onBucketChange,
  selectedJobCandidateId,
  onSelectCandidate,
  onRemoveCandidate,
  senderTz,
  totalCount = 0,
  fixCount = 0,
  warningCount = 0,
  onCommit,
  isCommitting,
  onClose,
}: RecipientListProps) {
  const total = preview?.candidates.length ?? 0;
  const counts = preview?.buckets ?? { no_issue: 0, warning: 0, fix: 0 };

  const [search, setSearch] = React.useState("");

  const filtered = React.useMemo(() => {
    if (!preview) return [];
    const q = search.trim().toLowerCase();
    return preview.candidates.filter((c) => {
      if ((c.bucket ?? "no_issue") !== activeBucket) return false;
      if (!q) return true;
      return (
        c.full_name.toLowerCase().includes(q) ||
        (c.current_company ?? "").toLowerCase().includes(q) ||
        (c.resolved_email ?? "").toLowerCase().includes(q)
      );
    });
  }, [preview, activeBucket, search]);

  const enrollDisabled =
    totalCount === 0 || fixCount > 0 || isCommitting || !onCommit;

  const listRef = React.useRef<HTMLUListElement>(null);
  const [scrolled, setScrolled] = React.useState(false);
  const handleScroll = React.useCallback(() => {
    const el = listRef.current;
    if (el) setScrolled(el.scrollTop > 8);
  }, []);
  const scrollToTop = React.useCallback(() => {
    listRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <aside
      className={cn(
        "flex w-[504px] shrink-0 flex-col border-r border-border bg-surface-header",
        // ARBI-677 mobile two-step: the list owns the full sheet width below
        // `lg` (the fixed 504px column forced horizontal overflow) and steps
        // aside entirely once a candidate is selected.
        "max-lg:w-full max-lg:border-r-0",
        selectedJobCandidateId && "max-lg:hidden",
      )}
    >
      <header className="flex h-[60px] shrink-0 items-center justify-between border-b border-border px-6 max-lg:px-4">
        <div className="flex items-center gap-2 text-foreground">
          <Icon
            name="IconGroup1"
            size={18}
            className="text-foreground opacity-75"
          />
          <span className="text-sm font-medium text-heading">
            Recipients ({total})
          </span>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close enrollment preview"
            className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
          >
            <Icon name="IconCrossMedium" size={16} />
          </button>
        ) : null}
      </header>

      <BucketCounters
        counts={counts}
        active={activeBucket}
        onSelect={onBucketChange}
      />

      <div className="flex flex-1 min-h-0 flex-col bg-[#232325]">
        <div className="px-6 pt-4 max-lg:px-4">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-elevated px-3 py-2">
            <Icon
              name="IconMagnifyingGlass"
              size={16}
              className="shrink-0 text-muted-foreground"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              aria-label="Search recipients"
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>

        <div className="relative flex-1 min-h-0">
          <AnimatePresence>
            {scrolled ? (
              <motion.div
                key="recipients-top-fade"
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="pointer-events-none absolute inset-x-0 top-0 z-10 h-10 bg-gradient-to-b from-[#232325] to-transparent"
              />
            ) : null}
          </AnimatePresence>
          <AnimatePresence>
            {scrolled ? (
              <motion.div
                key="recipients-scroll-top"
                className="absolute inset-x-0 top-2.5 z-20 flex justify-center"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
              >
                <button
                  type="button"
                  onClick={scrollToTop}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-surface-header px-3 py-1 text-xs font-medium text-muted-foreground shadow-[0px_4px_12px_0px_rgba(0,0,0,0.35)] transition-colors hover:text-foreground"
                >
                  <Icon name="IconArrowUp" size={12} />
                  Scroll back to top
                </button>
              </motion.div>
            ) : null}
          </AnimatePresence>
          <ul
            ref={listRef}
            onScroll={handleScroll}
            className="h-full space-y-2 overflow-y-auto px-6 pb-4 pt-4 max-lg:px-4"
            aria-label={`Recipients in bucket ${activeBucket}`}
          >
        {isLoading ? (
          <li className="flex items-center justify-center py-10 text-sm text-muted-foreground">
            Loading recipients…
          </li>
        ) : filtered.length === 0 ? (
          <li className="flex flex-col items-center rounded-md border border-border bg-surface-elevated p-6 text-center">
            {!search.trim() ? (
              <Icon
                name={BUCKET_META[activeBucket].icon}
                size={28}
                className="mb-3"
                style={{ color: BUCKET_ICON_COLOR[activeBucket] }}
              />
            ) : null}
            <p className="text-sm font-medium text-heading">
              {search.trim()
                ? "No matching recipients"
                : EMPTY_BUCKET_COPY[activeBucket].title}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {search.trim()
                ? "Try a different name, company, or email."
                : EMPTY_BUCKET_COPY[activeBucket].subtitle}
            </p>
          </li>
        ) : (
          filtered.map((candidate) => (
            <RecipientCard
              key={candidate.job_candidate_id}
              candidate={candidate}
              selected={selectedJobCandidateId === candidate.job_candidate_id}
              onSelect={() => onSelectCandidate(candidate.job_candidate_id)}
              onRemove={() => onRemoveCandidate(candidate.job_candidate_id)}
              senderTz={senderTz}
            />
          ))
        )}
          </ul>
        </div>
      </div>

      <footer className="flex h-[60px] shrink-0 items-center justify-between gap-3 border-t border-border px-6 max-lg:px-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {fixCount > 0 ? (
            <button
              type="button"
              onClick={() => onBucketChange("fix")}
              aria-label="Show candidates with issues to fix"
              className="inline-flex cursor-pointer items-center gap-1.5 text-destructive transition-opacity hover:opacity-80"
            >
              <Icon name="IconCircleX" size={14} />
              {fixCount} {fixCount === 1 ? "issue" : "issues"} to fix
            </button>
          ) : warningCount > 0 ? (
            <button
              type="button"
              onClick={() => onBucketChange("warning")}
              aria-label="Show candidates with warnings"
              className="inline-flex cursor-pointer items-center gap-1.5 text-amber-500 transition-opacity hover:opacity-80"
            >
              <Icon name="IconExclamationCircle" size={14} />
              {warningCount} {warningCount === 1 ? "warning" : "warnings"}
            </button>
          ) : null}
        </div>
        <SubmitButton
          type="button"
          variant="primary"
          size="sm"
          disabled={enrollDisabled}
          loading={isCommitting}
          loadingLabel="Enrolling"
          icon={<Icon name="IconPaperPlane" size={14} />}
          onClick={() => void (onCommit && onCommit())}
          aria-label={
            fixCount > 0
              ? "Resolve fix issues to enable enrollment"
              : `Enroll ${totalCount} to sequence`
          }
        >
          Enroll to Sequence
        </SubmitButton>
      </footer>
    </aside>
  );
}
